import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    TextField,
    FormControlLabel,
    Radio,
    RadioGroup,
    Button,
    FormControl,
    FormLabel,
    Snackbar,
    Alert,
    CircularProgress,
} from '@mui/material';
import {
  decryptText,
  decryptAppId,
} from "../../../utils/encryption";
import PageTitle from '../../../components/PageTitle';
import useAssessmentTracking from '../../../hooks/useAssessmentTracking';
import { LocationWarningDialog, HourlyAttendanceDialog } from '../../../components/AssessmentTrackingDialogs';

const NAF6Form = () => {
  const location = useLocation();
  
    const getOrgIdFromQuery = () => {
      const searchParams = new URLSearchParams(location.search);
      const encryptedOrgId = searchParams.get("orgId");
      if (encryptedOrgId) {
        try {
          const decryptedOrgId = decryptText(encryptedOrgId);
          console.log("📍 Using decrypted orgId from URL query:", decryptedOrgId);
          return decryptedOrgId;
        } catch (error) {
          return encryptedOrgId;
        }
      }
      return null;
    };
  
    // Function to get appId from URL query parameters with decryption
    const getAppIdFromQuery = () => {
      const searchParams = new URLSearchParams(location.search);
      const encryptedAppId = searchParams.get("appId");
      if (encryptedAppId) {
        try {
          const decryptedAppId = decryptAppId(encryptedAppId);
          return decryptedAppId;
        } catch (error) {
          return encryptedAppId;
        }
      }
      return null;
    };
  
    // Function to get assessmentId from URL query parameters with decryption
    const getAssessmentIdFromQuery = () => {
      const searchParams = new URLSearchParams(location.search);
      const encryptedAssessmentId = searchParams.get("assessmentId");
      if (encryptedAssessmentId) {
        try {
          const decryptedAssessmentId = decryptText(encryptedAssessmentId);
          console.log(
            "📍 Using decrypted assessmentId from URL query:",
            decryptedAssessmentId
          );
          return decryptedAssessmentId;
        } catch (error) {
          return encryptedAssessmentId;
        }
      }
      return null;
    };
  
    // Function to get assessorId from URL query parameters with decryption
    const getAssessorIdFromQuery = () => {
      const searchParams = new URLSearchParams(location.search);
      const encryptedAssessorId = searchParams.get("assessorId");
      if (encryptedAssessorId) {
        try {
          const decryptedAssessorId = decryptText(encryptedAssessorId);
          console.log(
            "📍 Using decrypted assessorId from URL query:",
            decryptedAssessorId
          );
          return decryptedAssessorId;
        } catch (error) {
          return encryptedAssessorId;
        }
      }
      return null;
    };
  
    const getOrgId = () => {
      const queryOrgId = getOrgIdFromQuery();
      if (queryOrgId) {
        return queryOrgId;
      }
      return null;
    };
  
    const getAppId = () => {
      const queryAppId = getAppIdFromQuery();
      if (queryAppId) {
        return queryAppId;
      }
      return null;
    };
  
    const getAssessmentId = () => {
      const queryAssessmentId = getAssessmentIdFromQuery();
      if (queryAssessmentId) {
        return queryAssessmentId;
      }
      return null;
    };
  
    const getAssessorId = () => {
      const queryAssessorId = getAssessorIdFromQuery();
      if (queryAssessorId) {
        return queryAssessorId;
      }
      return null;
    };
  
    const [orgId, setOrgId] = useState(getOrgId());
    const [appId, setAppId] = useState(getAppId());
    const [assessmentId, setAssessmentId] = useState(getAssessmentId());
    const [assessorId, setAssessorId] = useState(getAssessorId());
    const [naf6Id, setNaf6Id] = useState(null);
    const [assessmentTypeId, setAssessmentTypeId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
      open: false,
      message: '',
      severity: 'success',
    });

  // Assessment tracking hook for location and hourly attendance
  const {
    locationWarningOpen,
    distanceMessage,
    attendanceCheckDialogOpen,
    attendanceCheckMessage,
    setLocationWarningOpen,
    setAttendanceCheckDialogOpen,
    checkHourlyAttendance,
  } = useAssessmentTracking({
    assessorId,
    appId,
    orgId,
  });
  const [formData, setFormData] = useState({
    qualityManager: '',
    dateOfEarlierVisit: '',
    assessmentSummary: '',
    recommendation: '',
    recommendationRemark: '',
    nonConformitiesDischarged: '',
    compliance: {
      nabl133: '',
      nabl142: '',
      nabl163: ''
    },
    complianceRemarks: {
      nabl133: '',
      nabl142: '',
      nabl163: ''
    },
    recommendForAccreditation: '',
    correctiveActionsDate: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleComplianceChange = (standard, value) => {
    setFormData(prev => ({
      ...prev,
      compliance: {
        ...prev.compliance,
        [standard]: value
      }
    }));
  };

  const handleComplianceRemarkChange = (standard, value) => {
    setFormData(prev => ({
      ...prev,
      complianceRemarks: {
        ...prev.complianceRemarks,
        [standard]: value
      }
    }));
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // Fetch existing NAF6 data
  useEffect(() => {
    const fetchAssessmentType = async () => {
      if (!appId || !orgId) return;

      try {
        const url = `${process.env.REACT_APP_API_URL}/assessment/assessment-type/${appId}/${orgId}`;
        const response = await axios.get(url);

        if (response.data && response.data.success && response.data.data) {
          setAssessmentTypeId(response.data.data.AssessmentTypeId);
        }
      } catch (error) {
        console.error('Error fetching assessment type:', error);
      }
    };

    const fetchNAF6Data = async () => {
      if (!appId || !assessmentId || !assessorId) return;

      try {
        setLoading(true);
        const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetNAF6?appid=${appId}&assessmentid=${assessmentId}&assessorid=${assessorId}`;
        const response = await axios.get(url);

        if (response.data && response.data.isstatus && response.data) {
          const data = response.data;
          setNaf6Id(data.id);
          setFormData({
            qualityManager: data.qualityManager || '',
            dateOfEarlierVisit: data.dates || '',
            assessmentSummary: data.assessmentSummary || '',
            recommendation: data.recommendationsOfAssessmentTeam || '',
            recommendationRemark: data.assessmentTeamComment || '',
            nonConformitiesDischarged: data.nonConformities || '',
            compliance: {
              nabl133: data.nabl133Value || '',
              nabl142: data.nabl142Value || '',
              nabl163: data.nabl163Value || ''
            },
            complianceRemarks: {
              nabl133: data.nabl133NcStatement || '',
              nabl142: data.nabl142NcStatement || '',
              nabl163: data.nabl163NcStatement || ''
            },
            recommendForAccreditation: data.isLabRecommend || '',
            correctiveActionsDate: data.dateOfSubmission || ''
          });
        }
      } catch (error) {
        console.error('Error fetching NAF6 data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAssessmentType();
    fetchNAF6Data();
  }, [appId, assessmentId, assessorId, orgId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Submitting NAF6 form data:', orgId, appId, assessmentId, assessorId, formData);  
    
    if (!appId || !orgId || !assessorId || !assessmentId) {
      showSnackbar('Missing required parameters', 'error');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        appId: String(appId),
        orgId: String(orgId),
        assessorId: String(assessorId),
        assessmentId: String(assessmentId),
        dates: assessmentTypeId === 1 ? "" : formData.dateOfEarlierVisit,
        assessmentSummary: formData.assessmentSummary,
        assessmentTeamComment: formData.recommendationRemark,
        recommendationsOfAssessmentTeam: formData.recommendation,
        nonConformities: formData.nonConformitiesDischarged,
        nabl133Value: formData.compliance.nabl133,
        nabl133NcStatement: formData.complianceRemarks.nabl133,
        nabl142Value: formData.compliance.nabl142,
        nabl142NcStatement: formData.complianceRemarks.nabl142,
        nabl163Value: formData.compliance.nabl163,
        nabl163NcStatement: formData.complianceRemarks.nabl163,
        isLabRecommend: formData.recommendForAccreditation,
        dateOfSubmission: formData.recommendForAccreditation==='Yes' ? formData.correctiveActionsDate : '',
        recommended17: '',
        notRecommended17: '',
        remarks: '',
        qualityManager: formData.qualityManager,
      };

      let response;
      if (naf6Id) {
        // Update existing NAF6
        const updatePayload = { id: String(naf6Id), ...payload };
        const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/UpdateNAF6`;
        response = await axios.put(url, updatePayload);
      } else {
        // Create new NAF6
        const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/SaveNAF6`;
        response = await axios.post(url, payload);
      }

      if (response.data.success || response.data.isstatus) {
        showSnackbar(naf6Id ? 'NAF6 updated successfully!' : 'NAF6 saved successfully!', 'success');
        if (!naf6Id && response.data.id) {
          setNaf6Id(response.data.id);
        }
      } else {
        throw new Error(response.data.message || 'Failed to save NAF6');
      }
    } catch (error) {
      console.error('Error submitting NAF6:', error);
      showSnackbar(
        error.response?.data?.message || error.message || 'Error saving NAF6',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <PageTitle title="NABL | NAF-6" />
    <Box sx={{ p: 2, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          NAF 6
        </Typography>
      </Box>

      {/* Main Form Card */}
      <Card sx={{ mb: 3, boxShadow: 3, borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            {/* Basic Information Section */}
            <Card sx={{ mb: 3, boxShadow: 2, borderRadius: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: 'primary.main' }}>
                  Assessment Information
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={assessmentTypeId !== 1 ? 6 : 12}>
                    <TextField
                      fullWidth
                      label="Quality Manager"
                      value={formData.qualityManager}
                      onChange={(e) => handleInputChange('qualityManager', e.target.value)}
                      margin="normal"
                      required
                      placeholder="Enter quality manager name"
                    />
                  </Grid>
                  {assessmentTypeId !== 1 && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Date of Earlier Visit"
                        type="date"
                        value={formData.dateOfEarlierVisit}
                        onChange={(e) => handleInputChange('dateOfEarlierVisit', e.target.value)}
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                        required
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Assessment Summary"
                      value={formData.assessmentSummary}
                      onChange={(e) => handleInputChange('assessmentSummary', e.target.value)}
                      margin="normal"
                      multiline
                      rows={3}
                      required
                      placeholder="Provide detailed assessment summary"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Recommendation of Assessment Team"
                      value={formData.recommendation}
                      onChange={(e) => handleInputChange('recommendation', e.target.value)}
                      margin="normal"
                      multiline
                      rows={2}
                      required
                      placeholder="Enter assessment team recommendation"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Recommendation Remark"
                      value={formData.recommendationRemark}
                      onChange={(e) => handleInputChange('recommendationRemark', e.target.value)}
                      margin="normal"
                      multiline
                      rows={2}
                      required
                      placeholder="Additional remarks for recommendation"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Compliance Section */}
            <Card sx={{ mb: 3, boxShadow: 2, borderRadius: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: 'primary.main' }}>
                  Compliance Assessment
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControl component="fieldset" required>
                      <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 2 }}>
                        Non-Conformities during earlier Visit have been discharged
                      </FormLabel>
                      <RadioGroup
                        row
                        value={formData.nonConformitiesDischarged}
                        onChange={(e) => handleInputChange('nonConformitiesDischarged', e.target.value)}
                      >
                        <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                        <FormControlLabel value="No" control={<Radio />} label="No" />
                      </RadioGroup>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: 'text.primary' }}>
                      Assessment Team Comment on Compliance of laboratory to:
                    </Typography>
                    
                    {/* NABL 133 Compliance */}
                    <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={3}>
                          <FormLabel sx={{ fontWeight: 'bold' }}>(a) NABL 133*</FormLabel>
                        </Grid>
                        <Grid item xs={12} md={9}>
                          <RadioGroup
                            row
                            value={formData.compliance.nabl133}
                            onChange={(e) => handleComplianceChange('nabl133', e.target.value)}
                          >
                            <FormControlLabel value="Compliance" control={<Radio />} label="Compliance" />
                            <FormControlLabel value="Non Compliance" control={<Radio />} label="Non Compliance" />
                          </RadioGroup>
                          {formData.compliance.nabl133 === 'Non Compliance' && (
                            <TextField
                              fullWidth
                              label="Remarks for Non-Compliance"
                              value={formData.complianceRemarks.nabl133}
                              onChange={(e) => handleComplianceRemarkChange('nabl133', e.target.value)}
                              margin="normal"
                              multiline
                              rows={2}
                              required
                              placeholder="Please provide details for non-compliance..."
                            />
                          )}
                        </Grid>
                      </Grid>
                    </Card>

                    {/* NABL 142 Compliance */}
                    <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={3}>
                          <FormLabel sx={{ fontWeight: 'bold' }}>(b) NABL 142*</FormLabel>
                        </Grid>
                        <Grid item xs={12} md={9}>
                          <RadioGroup
                            row
                            value={formData.compliance.nabl142}
                            onChange={(e) => handleComplianceChange('nabl142', e.target.value)}
                          >
                            <FormControlLabel value="Compliance" control={<Radio />} label="Compliance" />
                            <FormControlLabel value="Non Compliance" control={<Radio />} label="Non Compliance" />
                          </RadioGroup>
                          {formData.compliance.nabl142 === 'Non Compliance' && (
                            <TextField
                              fullWidth
                              label="Remarks for Non-Compliance"
                              value={formData.complianceRemarks.nabl142}
                              onChange={(e) => handleComplianceRemarkChange('nabl142', e.target.value)}
                              margin="normal"
                              multiline
                              rows={2}
                              required
                              placeholder="Please provide details for non-compliance..."
                            />
                          )}
                        </Grid>
                      </Grid>
                    </Card>

                    {/* NABL 163 Compliance */}
                    <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={3}>
                          <FormLabel sx={{ fontWeight: 'bold' }}>(c) NABL 163*</FormLabel>
                        </Grid>
                        <Grid item xs={12} md={9}>
                          <RadioGroup
                            row
                            value={formData.compliance.nabl163}
                            onChange={(e) => handleComplianceChange('nabl163', e.target.value)}
                          >
                            <FormControlLabel value="Compliance" control={<Radio />} label="Compliance" />
                            <FormControlLabel value="Non Compliance" control={<Radio />} label="Non Compliance" />
                          </RadioGroup>
                          {formData.compliance.nabl163 === 'Non Compliance' && (
                            <TextField
                              fullWidth
                              label="Remarks for Non-Compliance"
                              value={formData.complianceRemarks.nabl163}
                              onChange={(e) => handleComplianceRemarkChange('nabl163', e.target.value)}
                              margin="normal"
                              multiline
                              rows={2}
                              required
                              placeholder="Please provide details for non-compliance..."
                            />
                          )}
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl component="fieldset" fullWidth required>
                      <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 2 }}>
                        Is this lab Recommended for Accreditation?
                      </FormLabel>
                      <RadioGroup
                        row
                        value={formData.recommendForAccreditation}
                        onChange={(e) => handleInputChange('recommendForAccreditation', e.target.value)}
                      >
                        <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                        <FormControlLabel value="No" control={<Radio />} label="No" />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                 {(formData.recommendForAccreditation === 'Yes') && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Date for Submission of Corrective actions by the laboratory for Non Conformities raised."
                      type="date"
                      value={formData.correctiveActionsDate}
                      onChange={(e) => handleInputChange('correctiveActionsDate', e.target.value)}
                      margin="normal"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min: new Date().toISOString().split('T')[0],
                        max: new Date(new Date().setDate(new Date().getDate() + 30)).toISOString().split('T')[0]
                      }}
                      required
                    />
                  </Grid>
                 )}
                </Grid>
              </CardContent>
            </Card>

            {/* Save Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ px: 6, py: 1.5, fontSize: '1.1rem' }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={24} sx={{ mr: 1 }} color="inherit" />
                    {naf6Id ? 'Updating...' : 'Saving...'}
                  </>
                ) : (
                  naf6Id ? 'Update' : 'Save'
                )}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Location Warning Dialog */}
      <LocationWarningDialog
        open={locationWarningOpen}
        onClose={() => setLocationWarningOpen(false)}
        distanceMessage={distanceMessage}
      />

      {/* Hourly Attendance Dialog */}
      <HourlyAttendanceDialog
        open={attendanceCheckDialogOpen}
        onClose={() => setAttendanceCheckDialogOpen(false)}
        message={attendanceCheckMessage}
        onRecheck={checkHourlyAttendance}
      />
    </Box>
    </>
  );
};

export default NAF6Form;